import React from 'react';
import PropTypes from 'prop-types';

import Loader from 'relements/build/esm/decorators/loader';
import Text from 'relements/build/esm/ui/text';

import connection from '@src/connection';

import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';

import { GOOGLE_ANALYTICS, PERMISSION_LEVELS } from '@src/const';
import {
  AlertTypes,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
  FEATURE_FLAG_DATA,
} from '@src/constants/MogamboConstants';

import BodyHeader from './components/BodyHeader';
import GeneralNodesBody from './components/GeneralNodesBody';
import EmptyNodes from './components/EmptyNodes';
import * as maps from './maps';
import './GeneralNodes.scss';

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
class GeneralNodesV2 extends React.Component {
  constructor() {
    super();

    this.listScrollRef = React.createRef();

    this.state = {
      edited: false,
      nodeData: [],
      editedNodeData: {},
      scrollExists: false, // For Add Node Button
      currentLanguage: 'en',
      currDomain: {},
    };
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.generalNodes &&
      this.props.generalNodes !== nextProps.generalNodes
    ) {
      const nodeData = {};
      const editedNodeData = {};

      nextProps.generalNodes.forEach((node) => {
        nodeData[node.node_id] = node;
      });

      nextProps.editedGeneralNodes?.forEach((node) => {
        nodeData[node.node_id] = node;
        editedNodeData[node.node_id] = node;
      });

      this.setState({
        edited: false,
        nodeData,
        editedNodeData,
      });
    }

    if (this.state.currDomain?.id !== nextProps?.domain?.id) {
      this.setState({
        currDomain: { ...nextProps.domain },
      });
    }
  }

  componentDidMount() {
    this.props.activateLoader();
    if (!this.props.loaderActive) this.getGeneralNodesData();
  }

  componentDidUpdate() {
    // To check whether a element is added or deleted affects the scroll height
    // Based on that update the CSS for Add another FAQ Button.
    setTimeout(this.onScroll, 400);
  }

  componentWillUnmount() {
    maps.clearGeneralNodes();
  }

  getGeneralNodesData() {
    this.props.activateLoader();
    maps.getGeneralNodes(
      this.props.match.params.botId,
      this.props.match.params.storyId,
      this.props.currentLanguage,
      () => {
        this.props.deactivateLoader();

        // To set the Box Class to Add Node Button if list is scrollable
        if (
          this.listScrollRef.current?.scrollHeight >
          this.listScrollRef.current?.clientHeight
        ) {
          this.setState({
            scrollExists: true,
          });
        }
      }
    );
  }

  // Scroll to Bottom on Add New Node
  scrollToBottom = () => {
    this.listScrollRef.current.scrollTo({
      top: this.listScrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  // Scroll Handler for Add New Node Button
  onScroll = () => {
    if (this.listScrollRef.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = this.listScrollRef.current;
      if (scrollTop + clientHeight <= scrollHeight - 1) {
        // Added a condition for unnecessary state update
        !this.state.scrollExists &&
          this.setState({
            scrollExists: true,
          });
      } else {
        // Added a condition for unnecessary state update
        this.state.scrollExists &&
          this.setState({
            scrollExists: false,
          });
      }
    }
  };

  render() {
    return (
      <div
        className="general-nodes"
        onScroll={this.onScroll}
        ref={this.listScrollRef}
      >
        {this.renderBody()}
      </div>
    );
  }

  // renders body of the page
  renderBody = () => {
    const orderedNodes = Object.values(this.state.nodeData).sort(
      (node1, node2) => {
        // Special handling for new node
        if (node1.new && node2.new) return 1;
        if (node1.new_node) return 1;
        if (node2.new_node) return -1;
        return node1.system_name > node2.system_name ? 1 : -1;
      }
    );

    return (
      <div className="general-nodes-body">
        {this.props.match.params.storyId ? (
          <div className="general-nodes-header-title">
            <Text variant="h1.semi-bold">Skill Steps</Text>
          </div>
        ) : null}
        <BodyHeader
          orderedNodes={orderedNodes}
          isSelfServe={this.props.isSelfServe}
          onSave={this.onSave}
          edited={this.state.edited}
        />

        {orderedNodes.length === 0 ? (
          <EmptyNodes onAddNodeClick={this.onAddNewNode} />
        ) : (
          <GeneralNodesBody
            orderedNodes={orderedNodes}
            scrollExists={this.state.scrollExists}
            onAddNewNode={this.onAddNewNode}
            activeIndex={this.state.activeIndex}
            toggleActive={this.toggleActive}
            handleToggle={this.handleToggle}
            dataUpdateCallback={this.dataUpdateCallback}
            updateCurrentSourceLanguage={this.updateCurrentSourceLanguage}
            {...this.props}
          />
        )}
      </div>
    );
  };

  // adds new node
  onAddNewNode = () => {
    const { nodeData, editedNodeData } = this.state;

    const randomId = Math.floor(Math.random() * 10000);
    const newObjectData = {
      node_id: randomId,
      system_name: '',
      bot_responses: [],
      responses: {},
      active: true,
      user_responses: [],
      new_node: true,
      [CONTEXT_CLEAR_NODE.KEY]: false,
      [END_OF_QUERY.KEY]: false,
    };

    this.props.supportedLanguages.map((language) => {
      if (!language.includes('|')) {
        const hybridLanguages = CommonStore.getAllHybridLanguages();
        const value = {
          bot_responses: [],
          quick_replies: [],
        };

        hybridLanguages[language].map((script) => {
          newObjectData.responses[language] = {
            ...newObjectData.responses[language],
            [script.script]: {},
          };
        });
      }
    });

    nodeData[randomId] = newObjectData;
    editedNodeData[randomId] = newObjectData;

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
      activeIndex: Object.keys(nodeData).length - 1,
    });

    // Calls scrollToBottom after the list renders
    setTimeout(this.scrollToBottom, 50);
  };

  // creates nodes from the uploaded file
  createNodesFromFile = (filename, data) => {
    const { nodeData, editedNodeData } = this.state;

    const qnaList = this.extractQNAFromTSVFile(data);
    if (!qnaList) {
      return;
    }

    const nodePrefix = filename.split('.')[0].toUpperCase().replace(' ', '_');

    const randomId = Math.floor(Math.random() * 10000);

    qnaList.forEach((qna, index) => {
      const question = qna[0];
      const answer = qna[1];
      const node = {
        node_id: randomId + index,
        system_name: `${nodePrefix}_${index + 1}`,
        bot_responses: [answer],
        active: true,
        user_responses: [
          {
            keywords: [],
            active: true,
            id: `new-sentence-${Math.random()}`,
            sentence: question,
          },
        ],
        new_node: true,
      };

      if (!this.props.nodeContextMenuEnabled) {
        node[CONTEXT_CLEAR_NODE.KEY] = true;
      } else {
        node[END_OF_QUERY.KEY] = true;
      }

      nodeData[node.node_id] = node;
      editedNodeData[node.node_id] = node;
    });

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
    });
  };

  extractQNAFromTSVFile = (data) => {
    const rowList = data.split('\n').map((data) => data.split('\t'));

    const rows = rowList.slice(1);
    const mappedRows = rows.map((row) => {
      if (row.length < 2) {
        return [];
      }
      return [row[0], row[1]];
    });

    const filteredRows = mappedRows.filter((row) => {
      return row.length === 2;
    });

    if (!filteredRows || filteredRows.length === 0) {
      maps.createAlert('Invalid/Empty TSV file provided. Please try again.');
      return null;
    }

    return filteredRows;
  };

  dataUpdateCallback = (
    nodeId,
    fieldName,
    data,
    language = 'en',
    script = ''
  ) => {
    switch (fieldName) {
      case 'bot_responses':
        if (
          !maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT) &&
          !maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_SAYS_EDIT)
        ) {
          maps.permissionErrorAlert();
          return;
        }
        break;

      default:
        if (!maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
          maps.permissionErrorAlert();
          return;
        }
        break;
    }

    // Todo: Maintain a copy of the edited node data
    const { nodeData, editedNodeData } = this.state;

    // Check for the bot_responses and quick_replies case separately
    if (fieldName == 'bot_responses' || fieldName == 'quick_replies') {
      if (!nodeData[nodeId].responses[language][script]) {
        nodeData[nodeId].responses[language][script] = {
          bot_responses: [],
          quick_replies: [],
        };
      }
      nodeData[nodeId].responses[language][script][fieldName] = data;
    } else {
      nodeData[nodeId][fieldName] = data;
    }

    if (editedNodeData[nodeId] === undefined) {
      editedNodeData[nodeId] = {
        node_id: nodeData[nodeId].node_id,
        active: nodeData[nodeId].active,
        system_name: nodeData[nodeId].system_name,
        title: nodeData[nodeId].title,
        bot_responses: nodeData[nodeId].bot_responses,
        responses: nodeData[nodeId].responses,
        node_entities: nodeData[nodeId].node_entities || {},
        user_responses: nodeData[nodeId].user_responses || [],
        quick_replies: nodeData[nodeId].quick_replies || [],
      };

      if (!this.props.nodeContextMenuEnabled) {
        editedNodeData[nodeId][CONTEXT_CLEAR_NODE.KEY] =
          nodeData[nodeId][CONTEXT_CLEAR_NODE.KEY];
      } else {
        editedNodeData[nodeId][END_OF_QUERY.KEY] =
          nodeData[nodeId][END_OF_QUERY.KEY];
      }
    }

    if (fieldName == 'bot_responses' || fieldName == 'quick_replies') {
      editedNodeData[nodeId].responses = nodeData[nodeId].responses;
    } else {
      editedNodeData[nodeId][fieldName] = nodeData[nodeId][fieldName];
    }

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
    });
  };

  // saves
  onSave = () => {
    const editedNodeList = Object.values(this.state.editedNodeData);
    let negativeAlert = false;
    const hybridLanguages = CommonStore.getAllHybridLanguages();
    const disableLanguageGuardrails = BotStore.getFeatureFlag(
      FEATURE_FLAG_DATA.FLAGS.DISABLE_LANGUAGE_GUARDRAILS
    );

    if (editedNodeList.length === 0) {
      maps.createAlert('Nothing has changed');
      return;
    }

    const nodeNameMap = {};
    let duplicateName = null;

    Object.values(this.state.nodeData).forEach((node) => {
      if (nodeNameMap[node.system_name]) {
        duplicateName = node.system_name;
        console.warn(`Duplicate FAQ Step Name: ${duplicateName}`);
      } else {
        nodeNameMap[node.system_name] = true;
      }
    });

    if (duplicateName) {
      maps.createAlert(
        `System Names should be unique. ${duplicateName} is duplicate.`,
        undefined,
        AlertTypes.NEGATIVE
      );
      return;
    }

    editedNodeList.forEach((node) => {
      if (!node.title) {
        maps.createAlert(
          'Step name cannot be empty.',
          undefined,
          AlertTypes.NEGATIVE
        );
        negativeAlert = true;
        return;
      }

      if (node.title.replace(/[^a-zA-Z0-9_]/g, '').length < 3) {
        maps.createAlert(
          'Please enter a valid step name of minimum 3 letters',
          undefined,
          AlertTypes.NEGATIVE
        );
        negativeAlert = true;
        return;
      }

      if (node.new_node && !node.system_name) {
        maps.createAlert(
          'System name cannot be empty.',
          undefined,
          AlertTypes.NEGATIVE
        );
        negativeAlert = true;
        return;
      }

      if (node.new_node && !/^[a-zA-Z0-9_:]+$/.test(node.system_name)) {
        maps.createAlert(
          `${node.title} is an invalid Step Name. It can only contain A-Z, 0-9 and _`,
          undefined,
          AlertTypes.NEGATIVE
        );
        negativeAlert = true;
        return;
      }

      if (node.user_responses) {
        // Clean the user responses
        node.user_responses = node.user_responses.filter((response) => {
          // Select only old sentences or new sentences if they have some content
          return (
            !response.id.startsWith('new-sentence') ||
            response.sentence.length > 0
          );
        });

        if (node.user_responses.length <= 0 && !disableLanguageGuardrails) {
          maps.createAlert(
            `Please add atleast 1 User Message for ${node.title}`,
            undefined,
            AlertTypes.NEGATIVE
          );
          negativeAlert = true;
          return;
        }

        node.user_responses.forEach((response) => {
          if (!response.sentence) {
            maps.createAlert(
              `User Message cannot be empty for ${node.title}`,
              undefined,
              AlertTypes.NEGATIVE
            );
            negativeAlert = true;
          }
        });
      }

      this.props.supportedLanguages.map((language) => {
        if (!language.includes('|'))
          Object.values(hybridLanguages[language]).map((scriptData) => {
            if (
              (!node.responses[language][scriptData.script] ||
                !node.responses[language][scriptData.script].bot_responses ||
                node.responses[language][scriptData.script].bot_responses
                  .length === 0) &&
              !disableLanguageGuardrails
            ) {
              maps.createAlert(
                `Please add atleast 1 Bot Responses for every language in ${node.title}`,
                undefined,
                AlertTypes.NEGATIVE
              );
              negativeAlert = true;
            }
          });
      });

      node.responses[this.state.currentLanguage]?.bot_responses?.forEach(
        (response) => {
          if (!response) {
            maps.createAlert(
              `Bot Responses cannot be empty for ${node.system_name}`,
              undefined,
              AlertTypes.NEGATIVE
            );
            negativeAlert = true;
          }
        }
      );
    });

    if (negativeAlert) {
      return;
    }

    let gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_BOT;
    let gaventLabel = GOOGLE_ANALYTICS.LABEL.BOT_ADD;

    if (this.props.match.params.storyId) {
      gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_STORY;
      gaventLabel = GOOGLE_ANALYTICS.LABEL.STORY_ADD;
    }
    maps.sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.GENERAL_NODES,
      gaEventAction,
      gaventLabel,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );

    this.props.activateLoader();
    maps.saveGeneralNodes(
      {
        pending: editedNodeList,
        success: [],
        failure: [],
      },
      this.props.match.params.botId,
      this.props.match.params.storyId,
      this.state.currentLanguage,
      (success) => {
        this.props.deactivateLoader();

        if (success) {
          this.setState({
            edited: false,
          });

          maps.createAlert(
            `Changes saved successfully`,
            undefined,
            AlertTypes.POSITIVE
          );
        }
      },
      this.props.meta.domain.title
    );
  };

  // function to activate/deactivate node
  toggleActive = (node) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dataUpdateCallback(node.node_id, 'active', !node.active);
  };

  // toggles between different accordian
  handleToggle = (newIndex) => {
    this.setState((state) => ({
      activeIndex: state.activeIndex === newIndex ? -1 : newIndex,
    }));

    /** onScroll is called again while toggling the active step
     * Because when a step is active then usually window gets a scroll
     * So to update the class for the Add New Button onScroll is called.
     */
    setTimeout(this.onScroll, 400);
  };

  // New Language Function
  updateCurrentSourceLanguage = (selectedLanguage) => {
    this.setState({ currentLanguage: selectedLanguage });
  };
}

GeneralNodesV2.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      storyId: PropTypes.string,
      botId: PropTypes.string,
    }),
  }),
  meta: PropTypes.shape({
    domain: PropTypes.string,
    story: PropTypes.string,
  }),
  location: PropTypes.shape({
    prevLocation: PropTypes.string,
    currentLocation: PropTypes.string,
  }),
  meanings: PropTypes.shape({}),
  synonyms: PropTypes.shape({}),
  generalNodes: PropTypes.shape(),
  editedGeneralNodes: PropTypes.arrayOf(),
  updatedSynonyms: PropTypes.arrayOf(),
  currentLanguage: PropTypes.shape({
    languageSource: PropTypes.string,
    languageScript: PropTypes.string,
  }),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  loaderActive: PropTypes.bool,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
};

GeneralNodesV2.defaultProps = {
  meanings: {},
  match: {},
  meta: {},
  location: {},
  synonyms: {},
  generalNodes: [],
  editedGeneralNodes: [],
  updatedSynonyms: {},
  currentLanguage: {},
  supportedLanguages: [],
  loaderActive: false,
  activateLoader: () => {},
  deactivateLoader: () => {},
};

export default GeneralNodesV2;
